import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as accountApi from '@/api/account';
import { api, setAccountAuthErrorHandler, setAuthToken } from '@/api/client';
import { identifyUser, initAnalytics, resetIdentity } from '@/lib/analytics';
import {
  clearAccount,
  mintInstallationId,
  persistAccount,
  persistInstallationId,
  readAccountToken,
  readAccountUsername,
  readInstallationId,
  type IdSource,
} from '@/lib/installation';
import { logger } from '@/lib/logger';
import { platform, type PlatformName } from '@/lib/platform';
import { registerForPush } from '@/lib/push';

interface HandshakeBody {
  installationId: string;
  platform: PlatformName;
  appVersion?: string;
}

interface HandshakeResponse {
  userId: string;
  installationId: string;
  isNew: boolean;
}

interface SessionRecoverResponse {
  userId: string;
  installationId: string;
}

/**
 * Web-only: if localStorage lost the id, try to recover it from the server's
 * httpOnly session cookie before minting a new one (which would orphan the
 * account). Returns null when there's no valid session cookie (404) or any
 * error — caller falls back to minting.
 */
async function recoverFromSession(): Promise<string | null> {
  try {
    const res = await api.get<SessionRecoverResponse>('/v1/auth/session');
    return res.installationId;
  } catch {
    return null;
  }
}

type Status = 'loading' | 'ready' | 'error';

interface InstallationContextValue {
  status: Status;
  installationId: string | null;
  userId: string | null;
  /** Set when boot fails so the UI can render an error state. */
  error: Error | null;
  // ── Optional account (cross-device sync) ──
  /** Username of the signed-in account, or null when running anonymously. */
  username: string | null;
  isSignedIn: boolean;
  /** Claim an account on THIS device's data (keeps current follows/saves). */
  claimAccount: (username: string, pin: string) => Promise<void>;
  /** Sign in to an existing account (reboots to load that account's data). */
  signIn: (username: string, pin: string) => Promise<void>;
  /** Sign out → revert to anonymous device identity (reboots). */
  signOut: () => Promise<void>;
}

// Exported so the marketing preview (AnimatedPhone) can wrap the real
// SavedScreen in a stub provider — it's the one preview screen that reads
// account state from context. The throw in useInstallation stays the app's
// safety net; the preview just satisfies it with fixture values.
// eslint-disable-next-line react-refresh/only-export-components
export const InstallationContext = createContext<InstallationContextValue | null>(null);

export type { InstallationContextValue };

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0';

/**
 * App-boot identity flow. Runs once at mount, in this order:
 *
 *   1. Read (or mint) the installation_id from Keychain/Keystore.
 *   2. Set it as the Bearer token on the API client so subsequent
 *      requests authenticate.
 *   3. Handshake with POST /v1/auth/installations — this is what
 *      creates the `users` row on cold install. Idempotent, so safe
 *      to call on every launch.
 *   4. Initialize PostHog with the same id (so server + analytics agree
 *      on identity).
 *   5. Register for push (no-op on web; native shows the OS prompt
 *      and POSTs the token to /v1/me/devices).
 *
 * Children are not rendered until status === 'ready'. If the handshake
 * fails (offline first launch, backend down) the provider surfaces an
 * error; the existing AppLoadErrorFallback in App.tsx already handles
 * an "API unreachable" state and is what users will see.
 */
export function InstallationProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [installationId, setInstallationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  // Guards against re-entrant reload loops if a stale account token 401s on
  // several in-flight requests at once.
  const revertingRef = useRef(false);

  useEffect(() => {
    // StrictMode fires this effect twice in dev. The handshake is
    // idempotent server-side and `initAnalytics` self-guards on its
    // own `initialized` flag, so the duplicate is harmless. The
    // `cancelled` flag below stops the first run's stale state writes
    // after the cleanup unmount.
    let cancelled = false;
    void (async () => {
      try {
        // 1. Read the existing id (native: secure store → Preferences fallback,
        //    healing the secure store; web: localStorage).
        const read = await readInstallationId();
        if (cancelled) return;

        let id = read.id;
        let source: IdSource | 'cookie' = read.source;

        // 2. Web fallback: localStorage was empty — try to recover the id from
        //    the httpOnly session cookie before minting a brand-new one.
        if (!id && platform.isWeb) {
          const recovered = await recoverFromSession();
          if (cancelled) return;
          if (recovered) {
            id = recovered;
            source = 'cookie';
          }
        }

        // 3. Genuinely new install — mint and store everywhere.
        if (!id) {
          id = await mintInstallationId();
          if (cancelled) return;
        } else if (source === 'cookie') {
          // Re-adopt the recovered id into localStorage so next boot is local.
          await persistInstallationId(id);
        }

        setInstallationId(id);
        setAuthToken(id);

        const body: HandshakeBody = {
          installationId: id,
          platform: platform.name,
          appVersion: APP_VERSION,
        };
        const res = await api.post<HandshakeResponse>('/v1/auth/installations', body);
        if (cancelled) return;
        setUserId(res.userId);

        // 4b. If an account token is stored on this device, switch the active
        //     bearer to it so the store loads that account's data. The install
        //     id row still exists, so sign-out / a stale-token 401 can revert.
        const acct = await readAccountToken();
        if (cancelled) return;
        if (acct) {
          setAuthToken(acct);
          setUsername(await readAccountUsername());
        }

        // If a stale account token ever 401s mid-session (account deleted /
        // rotated server-side), drop it and reboot under the install id — the
        // app must never brick on a bad account credential (spike P1 #6).
        setAccountAuthErrorHandler(() => {
          if (revertingRef.current) return;
          revertingRef.current = true;
          setAuthToken(id);
          void clearAccount().finally(() => window.location.reload());
        });

        initAnalytics(id);
        // Push registration is fire-and-forget — it can take seconds
        // for the OS to deliver the token, and the listeners stay
        // mounted for the app's lifetime.
        void registerForPush();

        setStatus('ready');
        // `source` is the session-continuity signal: 'primary' = normal,
        // 'fallback'/'cookie' = a save we'd otherwise have lost, 'none' = a
        // genuinely new install. Watch fallback/cookie rates to size the
        // real-world impact of SST-14.
        logger.info('installation handshake', { isNew: res.isNew, source });
      } catch (err) {
        if (cancelled) return;
        const e = err instanceof Error ? err : new Error(String(err));
        logger.error('installation handshake failed', e);
        setError(e);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      setAccountAuthErrorHandler(null);
    };
  }, []);

  // ── Account actions (consumed by the Saved-page account UI) ──────────────

  // Claim on this device: the account attaches to the current row, so the data
  // is unchanged — just switch the active bearer and show the signed-in state.
  const claimAccount = useCallback(async (uname: string, pin: string) => {
    const res = await accountApi.claimAccount(uname, pin);
    await persistAccount(res.accountToken, res.username);
    setAuthToken(res.accountToken);
    setUsername(res.username);
    // Claiming attaches the account to THIS device's existing user row, so the
    // id is unchanged — just (re)identify with the username as a person prop.
    identifyUser(res.userId, { username: res.username });
  }, []);

  // Sign in to an existing account: this loads a *different* user's data, so
  // reboot to refetch the whole store cleanly under the new token.
  const signIn = useCallback(async (uname: string, pin: string) => {
    const res = await accountApi.login(uname, pin);
    await persistAccount(res.accountToken, res.username);
    // Switching to a DIFFERENT account — reset so this device's prior person
    // doesn't merge into the new account. After the reload, the store
    // identifies the new account id cleanly from /v1/me.
    resetIdentity();
    window.location.reload();
  }, []);

  const signOut = useCallback(async () => {
    try {
      await accountApi.logout();
    } catch {
      /* server logout is best-effort; the client still drops its token */
    }
    await clearAccount();
    window.location.reload();
  }, []);

  const value = useMemo<InstallationContextValue>(
    () => ({
      status,
      installationId,
      userId,
      error,
      username,
      isSignedIn: username !== null,
      claimAccount,
      signIn,
      signOut,
    }),
    [status, installationId, userId, error, username, claimAccount, signIn, signOut],
  );

  return (
    <InstallationContext.Provider value={value}>{children}</InstallationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInstallation(): InstallationContextValue {
  const ctx = useContext(InstallationContext);
  if (!ctx) throw new Error('useInstallation must be used inside <InstallationProvider>');
  return ctx;
}
